/**
 * Premium Card Adapter
 * 
 * This component adapts the card to fit our million-dollar UI aesthetic
 * with subtle gradients, shadows, and animations matching the hero component.
 */
import React from 'react';
import { CardBaseProps, CardHeaderProps, CardFooterProps, CardContentProps } from '@repo/design-system/types';

// Import the shadcn Card components
import {
  Card as ShadcnCard,
  CardHeader as ShadcnCardHeader,
  CardFooter as ShadcnCardFooter,
  CardTitle as ShadcnCardTitle,
  CardDescription as ShadcnCardDescription,
  CardContent as ShadcnCardContent,
} from '../card';

// Import utils for class merging
import { cn } from '@/lib/utils';

// Note: Premium tokens are used directly in the classes below
// without importing them to avoid build errors

/**
 * Premium Card component
 */
export interface CardProps extends React.HTMLAttributes<HTMLDivElement>, CardBaseProps {
  fullWidth?: boolean;
  radius?: 'none' | 'sm' | 'md' | 'lg' | 'full';
  elevation?: 'none' | 'sm' | 'md' | 'lg';
  hover?: 'lift' | 'grow' | 'glow';
  animation?: 'fadeIn' | 'slideIn' | 'scaleIn';
  variant?: 'default' | 'bordered' | 'elevated' | 'glass';
  hoverable?: boolean;
}

/**
 * Premium Card Header component
 */
export interface CardHeaderComponentProps extends React.HTMLAttributes<HTMLDivElement>, CardHeaderProps {}

/**
 * Premium Card Footer component
 */
export interface CardFooterComponentProps extends React.HTMLAttributes<HTMLDivElement>, CardFooterProps {}

/**
 * Premium Card Content component
 */
export interface CardContentComponentProps extends React.HTMLAttributes<HTMLDivElement>, CardContentProps {}

/**
 * Premium Card Title component
 */
export interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  children?: React.ReactNode;
}

/**
 * Premium Card Description component
 */
export interface CardDescriptionProps extends React.HTMLAttributes<HTMLParagraphElement> {
  children?: React.ReactNode;
}

/**
 * Premium Card adapter
 *
 * This component adds luxury styling and subtle animations
 * to create a premium, polished user experience.
 * 
 * @example
 * ```tsx
 * <Card variant="glass" hoverable>
 *   <CardHeader>
 *     <CardTitle>Premium Card</CardTitle>
 *     <CardDescription>Luxury card with premium styling</CardDescription>
 *   </CardHeader>
 *   <CardContent>
 *     Content goes here
 *   </CardContent>
 *   <CardFooter>
 *     <Button>Action</Button>
 *   </CardFooter>
 * </Card>
 * ```
 */
const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', hoverable = false, fullWidth, radius, elevation, hover, animation, children, ...props }, ref) => {
    // Get the base classes
    const baseClasses = "overflow-hidden premium-transition-all";
    
    // Get variant-specific classes with premium styling
    const variantClasses = {
      default: "bg-white dark:bg-[#111111] premium-border premium-shadow-md dark:border-gray-800/60",
      bordered: "bg-white dark:bg-[#111111] premium-border dark:border-gray-800/80",
      elevated: "bg-white dark:bg-[#131313] premium-shadow-lg dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]",
      glass: "backdrop-blur-md bg-white/80 dark:bg-black/75 premium-shadow-glass dark:border-gray-800/40 premium-glass-elevated"
    };
    
    // Get hover effect classes with premium animation
    const hoverClasses = hoverable 
      ? "premium-hover-lift premium-card-animation cursor-pointer transform-gpu will-change-transform" 
      : "";
    
    // Get width classes
    const widthClasses = fullWidth 
      ? "w-full" 
      : "";
      
    // Get radius classes with correct premium format
    const radiusClasses = {
      none: "premium-radius-none",
      sm: "premium-radius-sm",
      md: "premium-radius-md",
      lg: "premium-radius-lg",
      xl: "premium-radius-xl",
      "2xl": "premium-radius-2xl",
      "3xl": "premium-radius-3xl",
      full: "premium-radius-full"
    };
    
    // Get radius class or default - use design system variables with stronger styling
    const radiusClass = radius 
      ? radiusClasses[radius] 
      : "premium-radius-card !important"; // Our premium default with high specificity
      
    // Get elevation classes with premium styling
    const elevationClasses = {
      none: "",
      sm: "premium-elevation-1 premium-card-elevated",
      md: "premium-elevation-2 premium-card-elevated",
      lg: "premium-elevation-3 premium-card-elevated"
    };
    
    // Get elevation class based on variant with premium effects
    const elevationClass = elevation 
      ? elevationClasses[elevation] 
      : variant === 'elevated' ? 'premium-elevation-3 premium-card-elevated premium-glass-elevated' : 
        variant === 'glass' ? 'premium-elevation-2 premium-glass-elevated' : 
        "premium-card-elevated";
      
    // Get hover animation classes with enhanced premium effects
    const hoverAnimationClasses = {
      lift: "premium-elevation-hover transform-gpu transition-all duration-300 will-change-transform premium-hover-lift",
      grow: "transform-gpu transition-all duration-300 hover:scale-105 will-change-transform",
      glow: "transition-all duration-300 premium-glass-elevated transform-gpu will-change-opacity"
    };
    
    // Get hover animation class with premium styling
    const hoverAnimationClass = hover 
      ? hoverAnimationClasses[hover] 
      : "";
      
    // Get animation classes
    const animationClasses = {
      fadeIn: "animate-fade-in",
      slideIn: "animate-slide-in",
      scaleIn: "animate-scale-in"
    };
    
    // Get animation class if specified
    const animationClass = animation 
      ? animationClasses[animation] 
      : "";
    
    // Get premium shadow classes for enhanced depth
    const premiumShadowClasses = {
      default: "shadow-[0_4px_16px_-2px_rgba(0,0,0,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]",
      primary: "shadow-[0_4px_16px_-2px_rgba(255,82,82,0.08)] dark:shadow-[0_8px_30px_rgba(0,0,0,0.4)]",
      hover: "hover:shadow-[0_10px_25px_-5px_rgba(0,0,0,0.1)] dark:hover:shadow-[0_15px_35px_-5px_rgba(0,0,0,0.3)]",
      primaryHover: "hover:shadow-[0_10px_25px_-5px_rgba(255,82,82,0.15)] dark:hover:shadow-[0_15px_35px_-5px_rgba(255,82,82,0.25)]"
    };
    
    return (
      <ShadcnCard
        ref={ref}
        className={cn(
          baseClasses,
          variantClasses[variant as keyof typeof variantClasses] || variantClasses.default,
          hoverClasses,
          widthClasses,
          radiusClass,
          elevationClass,
          hoverAnimationClass,
          animationClass,
          hover === 'lift' && premiumShadowClasses.primaryHover,
          hover === 'glow' && premiumShadowClasses.primaryHover,
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCard>
    );
  }
);
Card.displayName = "Card";

/**
 * Premium Card Header component
 */
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardHeader 
        ref={ref}
        className={cn(
          "premium-card-header",
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCardHeader>
    );
  }
);
CardHeader.displayName = "CardHeader";

/**
 * Premium Card Footer component
 */
const CardFooter = React.forwardRef<HTMLDivElement, CardFooterComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardFooter 
        ref={ref}
        className={cn(
          "premium-card-footer",
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCardFooter>
    );
  }
);
CardFooter.displayName = "CardFooter";

/**
 * Premium Card Content component
 */
const CardContent = React.forwardRef<HTMLDivElement, CardContentComponentProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardContent 
        ref={ref}
        className={cn(
          "premium-card-content",
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCardContent>
    );
  }
);
CardContent.displayName = "CardContent";

/**
 * Premium Card Title component
 */
const CardTitle = React.forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardTitle 
        ref={ref}
        className={cn(
          "gradient-text-primary font-bold",
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCardTitle>
    );
  }
);
CardTitle.displayName = "CardTitle";

/**
 * Premium Card Description component
 */
const CardDescription = React.forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <ShadcnCardDescription 
        ref={ref}
        className={cn(
          "text-gray-500 dark:text-gray-400",
          className
        )}
        {...props}
      >
        {children}
      </ShadcnCardDescription>
    );
  }
);
CardDescription.displayName = "CardDescription";

export { 
  Card, 
  CardHeader, 
  CardFooter, 
  CardTitle, 
  CardDescription, 
  CardContent 
};
